import { useAuthContext } from '@caterinx/api-client';
import { formatVatRate } from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RfqFacts } from '../components/RfqFacts.js';
import { authQuery } from '../lib/redirect.js';
import { claimExternalInvite, fetchExternalInvite, formatDeadline, formatRemaining } from '../lib/rfq.js';

/**
 * `/auftrag/:rfqId?token=…` — die Einladung an einen Betrieb, der noch kein Konto hat (D32).
 *
 * Der Betreiber hat angerufen und eine Mail geschickt; die Mail trägt diesen Link. Die Seite
 * zeigt den Auftrag **vor** jeder Anmeldung und führt mit möglichst wenig Reibung in die
 * gewöhnliche Angebotsmaske (`/ausschreibung/:rfqId?token=…`, mit dem Angebotslink, den das
 * Annehmen ausstellt).
 *
 * ### Der Weg durch die Anmeldung
 *
 * Wer kein Konto hat, registriert sich mit `redirect` auf diese Seite samt `annehmen=1`. Der
 * Parameter wandert durch Registrierung, E-Mail-Bestätigung und Login; zurück hier löst die
 * Seite das Annehmen **einmal selbst** aus. Ohne das stünde der Gastronom nach vier Schritten
 * wieder vor einem Knopf, den er schon einmal gedrückt hat.
 *
 * ### Warum das Einladungstoken nur hier und beim Annehmen mitgeht
 *
 * Es ist der Ausweis dieser einen Einladung. Danach ist der Betrieb ganz gewöhnlich eingeladen,
 * und die Angebotsmaske läuft über seinen eigenen Angebotslink.
 */
export function ExternalInvitePage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const autoClaim = searchParams.get('annehmen') === '1';
  const { token: authToken, isAuthLoading } = useAuthContext();
  const navigate = useNavigate();

  const {
    data: invite,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['external-invite', rfqId, token],
    enabled: rfqId !== undefined && token !== '',
    retry: false,
    queryFn: () => fetchExternalInvite(rfqId as string, token),
  });

  const isLoggedIn = authToken !== null;
  const isOpen = invite?.status === 'broadcast';

  /** Wohin Registrierung und Login zurückführen — und mit `annehmen=1` direkt zum Annehmen. */
  const redirectTarget = `/auftrag/${encodeURIComponent(rfqId ?? '')}?${new URLSearchParams({ token, annehmen: '1' }).toString()}`;
  const authParams = (withCompany: boolean): string =>
    authQuery({
      email: invite?.email,
      company: withCompany ? invite?.companyName : undefined,
      redirect: redirectTarget,
    });

  const claim = useMutation({
    mutationFn: () => claimExternalInvite(rfqId as string, token),
    /**
     * Mit dem Angebotslink, den das Annehmen ausstellt (D7). Seit D33 ginge es auch über die
     * Sitzung — der Link trägt aber auch dann noch, wenn sie abläuft, während der Caterer sein
     * erstes Angebot schreibt. Die Anmeldung bleibt nützlich: Menüvorschläge hochladen geht nur
     * angemeldet.
     */
    onSuccess: (result) =>
      navigate(`/ausschreibung/${encodeURIComponent(result.rfqId)}?token=${encodeURIComponent(result.offerToken)}`, {
        replace: true,
      }),
    onError: (error: unknown) => {
      // Sitzung abgelaufen: Der Weg zurück führt über den Login, mit demselben Ziel.
      if (isAxiosError(error) && error.response?.status === 401) {
        navigate(`/login?${authParams(false)}`, { replace: true });
      }
    },
  });

  /**
   * Das automatische Annehmen nach der Rückkehr aus Registrierung oder Login. Der Guard steht
   * in einem Ref, nicht im State: StrictMode führt Effekte zweimal aus, und ein erneutes
   * Rendern darf den Claim nicht ein zweites Mal auslösen — er ist beim Server zwar
   * wiederholbar, aber eine doppelte Anfrage wäre ein Zufallsfehler in der Anzeige.
   */
  const autoClaimTriggered = useRef(false);
  const canAutoClaim = autoClaim && isLoggedIn && isOpen;
  const { mutate: startClaim } = claim;
  useEffect(() => {
    if (!canAutoClaim || autoClaimTriggered.current) return;
    autoClaimTriggered.current = true;
    startClaim();
  }, [canAutoClaim, startClaim]);

  if (token === '' || isError) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTitle>Einladung nicht gefunden</AlertTitle>
          <AlertDescription>
            Der Link ist ungültig oder wurde durch einen neueren ersetzt. Melden Sie sich bei uns:{' '}
            <a href="mailto:angebote@caterinx.com" className="underline">
              angebote@caterinx.com
            </a>
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  if (isPending || isAuthLoading || !invite) {
    return (
      <Shell>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-4 h-48 w-full" />
      </Shell>
    );
  }

  const claimData = isAxiosError(claim.error)
    ? (claim.error.response?.data as { message?: string } | undefined)
    : undefined;
  const isClaiming = claim.isPending || (canAutoClaim && !claim.isError);

  return (
    <Shell>
      <h1 className="text-2xl font-semibold">Ein Catering-Auftrag für {invite.companyName || 'Ihren Betrieb'}</h1>
      <p className="text-muted-foreground mt-2 mb-6 text-sm">
        caterinx vermittelt Catering-Aufträge von Firmen- und Privatkunden an Betriebe wie Ihren. Die Registrierung ist
        kostenlos — Provision fällt nur an, wenn Sie den Zuschlag bekommen.
      </p>

      {isOpen && invite.bidsCloseAt ? (
        <Alert className="mb-6">
          <AlertTitle>Angebotsfrist: {formatRemaining(invite.bidsCloseAt)}</AlertTitle>
          <AlertDescription>Angebote werden angenommen bis {formatDeadline(invite.bidsCloseAt)}.</AlertDescription>
        </Alert>
      ) : (
        !isOpen && (
          <Alert className="mb-6" variant="destructive">
            <AlertTitle>Angebotsphase beendet</AlertTitle>
            <AlertDescription>Die Angebotsphase für diesen Auftrag ist beendet.</AlertDescription>
          </Alert>
        )
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{invite.brief?.title ?? 'Der Auftrag'}</CardTitle>
          {invite.brief && <CardDescription>{invite.brief.summary}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          <RfqFacts rfq={invite} />
          <p className="text-muted-foreground text-xs">
            Bei Zuschlag berechnet caterinx {formatVatRate(invite.commissionRateBp)} Vermittlungsentgelt auf Ihre
            Angebotssumme.
          </p>
        </CardContent>
      </Card>

      {isClaiming ? (
        <Alert className="mb-6">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <AlertTitle>Einladung wird angenommen …</AlertTitle>
        </Alert>
      ) : (
        <div className="space-y-4">
          {claim.isError && (
            <Alert variant="destructive">
              <AlertTitle>Die Einladung konnte nicht angenommen werden</AlertTitle>
              <AlertDescription>
                {claimData?.message ?? 'Bitte versuchen Sie es in einem Moment erneut.'}
              </AlertDescription>
            </Alert>
          )}

          {!isOpen ? (
            !isLoggedIn && (
              <Button asChild variant="outline" className="min-h-11 w-full sm:w-auto">
                <Link to="/register">Trotzdem registrieren</Link>
              </Button>
            )
          ) : isLoggedIn ? (
            <Button className="min-h-11 w-full sm:w-auto" onClick={() => claim.mutate()}>
              Einladung annehmen und Angebot abgeben
            </Button>
          ) : (
            <>
              {invite.claimed && (
                <Alert>
                  <AlertDescription>
                    Diese Einladung wurde bereits angenommen. Melden Sie sich mit dem Konto an, mit dem Sie sie
                    angenommen haben.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex flex-col gap-3 sm:flex-row">
                {!invite.claimed && (
                  <Button asChild className="min-h-11 w-full sm:w-auto">
                    <Link to={`/register?${authParams(true)}`}>Kostenlos registrieren und Angebot abgeben</Link>
                  </Button>
                )}
                <Button asChild variant={invite.claimed ? 'default' : 'outline'} className="min-h-11 w-full sm:w-auto">
                  <Link to={`/login?${authParams(false)}`}>Ich habe schon ein Konto — anmelden</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <Wordmark className="mb-8 w-40" />
      {children}
    </main>
  );
}
